import React, { useState, useEffect } from 'react';
import { Users, Gift, Clock, Check, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '../utils/priceFormat';

interface ReferralHistoryCardProps {
  className?: string;
}

interface Referral {
  id: string;
  status: 'pending' | 'completed';
  created_at: string;
  completed_at: string | null;
  referred_id: string;
  referred: { email: string } | null;
  subscription_id: string | null;
}

interface CreditTransaction {
  id: string;
  amount: number;
  created_at: string;
  description: string;
  reference_id: string | null;
}

interface ReferralHistoryData {
  referrals: Referral[];
  creditTransactions: CreditTransaction[];
  creditBalance: number;
  referralsCount: {
    total: number;
    completed: number;
  };
}

export const ReferralHistoryCard: React.FC<ReferralHistoryCardProps> = ({ className }) => {
  const { t } = useTranslation();
  const [historyData, setHistoryData] = useState<ReferralHistoryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchReferralHistory = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/referrals/history', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch referral history');
        }
        
        const data = await response.json() as ReferralHistoryData;
        setHistoryData(data);
      } catch (err) {
        console.error('Error fetching referral history:', err);
        setError(t('account.referral.historyLoadError'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchReferralHistory();
  }, [t]);
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  if (loading) {
    return (
      <div className={`bg-white p-6 rounded-xl shadow-sm animate-pulse ${className}`}>
        <div className="h-7 w-2/3 bg-gray-200 rounded mb-4"></div>
        
        <div className="grid grid-cols-2 gap-4 mb-5">
          <div className="h-20 bg-gray-200 rounded-xl"></div>
          <div className="h-20 bg-gray-200 rounded-xl"></div>
        </div>
        
        <div className="h-5 w-1/3 bg-gray-200 rounded mb-3"></div>
        <div className="h-32 bg-gray-200 rounded-xl mb-5"></div>
        
        <div className="h-5 w-1/3 bg-gray-200 rounded mb-3"></div>
        <div className="h-32 bg-gray-200 rounded-xl"></div>
      </div>
    );
  }
  
  if (error || !historyData) {
    return (
      <div className={`bg-white p-6 rounded-xl shadow-sm ${className}`}>
        <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('account.referral.history')}</h3>
        <p className="text-red-500 mb-4">{error || 'Failed to load referral history'}</p>
        <button 
          className="text-blue-600 hover:text-blue-800" 
          onClick={() => window.location.reload()}
        >
          {t('account.referral.tryAgain')}
        </button>
      </div>
    );
  }
  
  return (
    <div className={`bg-white p-6 rounded-xl shadow-sm ${className}`}>
      <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('account.referral.history')}</h3>
      
      {/* Referral Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="text-center p-4 bg-blue-50 rounded-xl">
          <div className="text-gray-500 text-sm mb-1">{t('account.referral.totalReferrals')}</div>
          <div className="text-3xl font-semibold">{historyData.referralsCount.total}</div>
        </div>
        <div className="text-center p-4 bg-green-50 rounded-xl">
          <div className="text-gray-500 text-sm mb-1">{t('account.referral.successfulReferrals')}</div>
          <div className="text-3xl font-semibold">{historyData.referralsCount.completed}</div>
        </div>
      </div>
      
      {/* Referrals List */}
      {historyData.referrals.length > 0 ? (
        <div className="mb-6">
          <h4 className="text-lg font-medium text-gray-800 mb-3 flex items-center">
            <Users size={18} className="mr-2" />
            {t('account.referral.yourReferrals')}
          </h4>
          <div className="border border-gray-200 rounded-xl divide-y divide-gray-200 overflow-hidden">
            {historyData.referrals.map(referral => (
              <div key={referral.id} className="p-4 bg-white">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium">
                      {referral.referred?.email || t('account.referral.unknownUser')}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('account.referral.invited')}: {formatDate(referral.created_at)}
                    </p>
                  </div>
                  <div className={`flex items-center px-3 py-1 rounded-full text-xs ${
                    referral.status === 'completed' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {referral.status === 'completed' ? (
                      <>
                        <Check size={14} className="mr-1" />
                        {t('account.referral.completed')}
                      </>
                    ) : (
                      <>
                        <Clock size={14} className="mr-1" />
                        {t('account.referral.pending')}
                      </>
                    )}
                  </div>
                </div>
                {referral.completed_at && (
                  <p className="text-xs text-gray-500 mt-1">
                    {t('account.referral.completed')}: {formatDate(referral.completed_at)}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 p-4 rounded-xl mb-6 text-center">
          <p className="text-gray-600">
            {t('account.referral.noReferralsYet')}
          </p>
        </div>
      )}
      
      {/* Credit Transactions */}
      {historyData.creditTransactions.length > 0 ? (
        <div>
          <h4 className="text-lg font-medium text-gray-800 mb-3 flex items-center">
            <Gift size={18} className="mr-2" />
            {t('account.referral.referralRewards')}
          </h4>
          <div className="border border-gray-200 rounded-xl divide-y divide-gray-200 overflow-hidden">
            {historyData.creditTransactions.map(transaction => (
              <div key={transaction.id} className="p-4 bg-white">
                <div className="flex justify-between">
                  <div>
                    <p className="text-sm font-medium">
                      ฿{formatPrice(transaction.amount, 'thb')} {t('account.referral.creditEarned')}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatDate(transaction.created_at)}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  {transaction.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 p-4 rounded-xl text-center">
          <p className="text-gray-600">
            {t('account.referral.noRewardsYet')}
          </p>
        </div>
      )}
    </div>
  );
}; 